import {Chart} from "chart.js/auto";
import {Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy} from "@angular/core";
import {AppTranslatePipe} from "../pipe/AppTranslatePipe";
import {ModeratAnfrage} from "../moderat/anfrage/moderat.anfrage";


@Component({
    selector: 'app-line-chart',
    templateUrl: './line-chart.component.html',
    providers: [AppTranslatePipe],
    standalone: false
})

export class LineChartComponent implements OnInit, OnChanges, OnDestroy {

  /**
   * holds the anfrage object
   */
  @Input() anfrage: ModeratAnfrage;

  /**
   * loading indicator
   */
  @Input() loading: boolean;

  /**
   * prefix for textkeys
   */
  textkeyPrefix: string = 'moderat.sanierung.linechart.';

  /**
   * holds the data for the chart
   */
  data: number[] = [];

  /**
   * holds data sets for the chart
   */
  dataSets: any = {};

  /**
   * holds the years for the chart
   */
  year: number[] = [];

  /**
   * holds the colors for the chart
   */
  colors: any = {};

  /**
   * toggle for the hint text
   */
  showHint: boolean = false;

  /**
   * toggle for the additional heating
   */
  hasAdditionalHeating: boolean = false;


  /**
   * holds the demand forecast data for energy costs
   */
  demandForecast: any = {};

  /**
   * holds the recommend forecast data for energy costs
   */
  recommendForecast: any = {};

  /**
   * holds the chart object
   */
  chart: Chart;

  /**
   * constructor
   * @param translate
   */
  constructor(private translate: AppTranslatePipe) {}

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    // check if there is additional heating and consumption for it
    this.hasAdditionalHeating = this.anfrage?.verbrauchsanalyseInput?.zusatzheizung_vorhanden === 'ja' && this.anfrage?.verbrauchsanalyseInput?.zusatzheizung_verbrauch > 0;

    // get the colors from the css variables
    const style = getComputedStyle(document.documentElement);
    this.colors = {
      primary: style.getPropertyValue('--color-primary'),
      secondary: style.getPropertyValue('--color-secondary'),
      lightGray: style.getPropertyValue('--color-light-gray'),
      darkerGray: style.getPropertyValue('--color-dark-gray'),
      bodyFontColor: style.getPropertyValue('--color-body-font-color'),
      white: style.getPropertyValue('--color-white'),
      xAxis: style.getPropertyValue('--color-x-axis'),
      yAxis: style.getPropertyValue('--color-y-axis'),
      yAxisTicks: style.getPropertyValue('--color-y-axis-ticks'),
      extendChartLines: style.getPropertyValue('--color-extend-chart-lines'),
    };

    // get the demand and recommend forecast data for energy costs (initial load)
    this.demandForecast = this.anfrage?.measureResult?.demand?.forecast_energy_costs;
    this.recommendForecast = this.anfrage?.measureResult?.recommend?.forecast_energy_costs;

    this.createChart();
  }

  /**
   * creates the chart with the given data including styling
   */
  createChart() {
    this.updateChartData();

    // legend margin plugin
    const legendMarginPlugin = {
      id: 'legendMargin',
      beforeOptionsLimit: (chart) => {
        const yAxis = chart.scales.y;
        if (yAxis) {
          // get the dynamic width of the Y-axis (including ticks & padding)
          const yWidth = yAxis.width;
        }
      },
      beforeDraw: (chart) => {
        const { legend, chartArea } = chart;
        if (legend && chartArea && window.innerWidth > 430) {
          // override the legend’s X‑position right before drawing so that it starts exactly at the left edge of the chartArea
          legend.left = chartArea.left;
        }
      }
    };

    // plugin to extend x-axis line and horizontal grid lines beyond last data point
    const extendXAxisPlugin = {
      id: 'extendXAxis',
      afterDraw: (chart) => {
        const ctx = chart.ctx;
        const xAxis = chart.scales.x;
        const yAxis = chart.scales.y;

        if (xAxis && yAxis) {
          const extendBy = 50; // pixels to extend
          const xEnd = xAxis.right; // end of x-axis

          ctx.save();

          // Extend all horizontal grid lines (y-axis ticks)
          const yTicks = yAxis.ticks;
          yTicks.forEach((tick, index) => {
            const yPosition = yAxis.getPixelForTick(index);

            // Skip the tick if it's at the same position as the x-axis bottom
            if (yPosition === yAxis.bottom) {
              return; // Skip this iteration
            }
            ctx.strokeStyle = this.colors.extendChartLines;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(xEnd, yPosition);
            ctx.lineTo(xEnd + extendBy, yPosition);
            ctx.stroke();
          });

          // Extend the x-axis line at the bottom
          ctx.strokeStyle = this.colors.xAxis;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(xEnd, yAxis.bottom);
          ctx.lineTo(xEnd + extendBy, yAxis.bottom);
          ctx.stroke();

          ctx.restore();
        }
      }
    };


    // create the chart with the given data and styling
    this.chart = new Chart("lineChart", {
      type: 'line',
      data: {
        labels: this.year,
        datasets: this.dataSets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        color: this.colors.bodyFontColor,
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: this.colors.yAxisTicks,
              drawTicks: false,
              lineWidth: 2
            },
            border: {
              color: this.colors.lightGray,
              width: 2
            },
            ticks: {
              callback: function(value, index, ticks) {
                return value.toLocaleString('de-DE') + ' €';
              },
              color: this.colors.bodyFontColor,
              padding: 10,
              align: 'end',
              font: {
                size: 16
              }
            }
          },
          x: {
            grid: {
              color: this.colors.xAxis,
              lineWidth: 2,
              tickLength: 25,
              drawOnChartArea: false
            },
            border: {
              color: this.colors.xAxis,
              width: 2
            },
            clip: false,
            ticks: {
              align: 'start',
              labelOffset: 10,
              color: this.colors.bodyFontColor,
              padding: 10,
              font: {
                size: 16
              }
            }
          }
        },
        interaction: {
          intersect: false
        },
        elements: {
          point: {
            radius: 0,
            hoverRadius: 0
          },
          line: {
            borderWidth: 4,
            fill: false,
            borderCapStyle: 'round',
            borderJoinStyle: 'round',
          }
        },
        plugins: {
          legend: {
            position: 'bottom',
            reverse: true,
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxHeight: 6,
              boxWidth: 6,
              font: {
                size: 16,
                weight: 'bold'
              }
            },
            align: 'start',
          },
          tooltip: {
            enabled: false,
          }
        }
      },
      plugins: [legendMarginPlugin, extendXAxisPlugin]
    });
  }


  /**
   * @inheritDoc
   */
  ngOnChanges(changes: SimpleChanges): void {
    // only update when loading finishes (loading changes from true to false)
    if (changes.loading && !changes.loading.firstChange && changes.loading.currentValue === false) {
      this.updateChart();
    }
  }

  /**
   * update energy costs from result.
   */
  updateChart(): void {
    // load the updated forecast data from the API response
    if (this.anfrage?.measureResult) {
      const result = this.anfrage.measureResult;

      // on changes, use costs.selection for demand forecast (user's selection)
      this.demandForecast = result.demand?.forecast_energy_costs;
      this.recommendForecast = result.costs?.selection?.forecast_energy_costs ?? result.recommend?.forecast_energy_costs;
    }

    // update the chart data arrays
    this.updateChartData();

    // update the Chart.js instance
    if (this.chart) {
      this.chart.data.labels = this.year;
      this.chart.data.datasets = this.dataSets;
      this.chart.update();
    }
  }

  /**
   * update the chart data arrays from the forecast data.
   * @private
   */
  updateChartData(): void {
    // clear the arrays to avoid duplicate data
    this.year = [];
    const recommendEnergyCosts = [];
    const demandEnergyCosts = [];

    // ensure we have valid forecast data
    if (!this.demandForecast || !this.recommendForecast) {
      return;
    }

    // create new array for the demand forecast data
    for (const forecast of this.demandForecast) {
      this.year.push(forecast.jahr);
      demandEnergyCosts.push(forecast.energie_kosten_jahr + forecast.co2_kosten_jahr);
    }

    // create new array for the recommend forecast data
    for (const forecast of this.recommendForecast) {
      recommendEnergyCosts.push(forecast.energie_kosten_jahr + forecast.co2_kosten_jahr);
    }

    // update the datasets
    this.dataSets = [
      {
        label: this.translate.transform(this.textkeyPrefix + 'mit.modernisierung'),
        data: recommendEnergyCosts,
        borderColor: this.colors.primary,
        backgroundColor: this.colors.primary
      },
      {
        label: this.translate.transform(this.textkeyPrefix + 'ohne.modernisierung'),
        data: demandEnergyCosts,
        borderColor: this.colors.darkerGray,
        backgroundColor: this.colors.darkerGray
      }
    ];
  }

  /**
   * Cleanup on component destruction
   */
  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }
}
